/**
 * Bound shape ids for a board link line (arrow start/end terminals).
 */
export function getArrowBoundShapeIds(editor, arrowId) {
  if (!editor || !arrowId) return [];
  const bindings = editor.getBindingsFromShape?.(arrowId, 'arrow') || [];
  const ids = [];
  const seen = new Set();
  for (const binding of bindings) {
    const terminal = binding?.props?.terminal;
    const toId = binding?.toId;
    if ((terminal !== 'start' && terminal !== 'end') || !toId || seen.has(toId)) continue;
    if (!editor.getShape(toId)) continue;
    seen.add(toId);
    ids.push(toId);
  }
  return ids;
}

/**
 * True when selection is one arrow, optionally plus only that arrow's bound shapes.
 * Bound shapes are no longer auto-selected, but tolerate them if the user adds them.
 */
export function getSelectedLinkArrowId(editor) {
  const ids = editor?.getSelectedShapeIds?.() || [];
  if (!ids.length) return null;

  let arrowId = null;
  for (const id of ids) {
    if (editor.getShape(id)?.type !== 'arrow') continue;
    if (arrowId) return null;
    arrowId = id;
  }
  if (!arrowId) return null;

  const bound = new Set(getArrowBoundShapeIds(editor, arrowId));
  for (const id of ids) {
    if (id === arrowId) continue;
    if (!bound.has(id)) return null;
  }
  return arrowId;
}

const BOUND_HIGHLIGHT_CLASS = 'board-arrow-bound-highlight';

function shapeDomId(el) {
  return el?.getAttribute?.('data-shape-id') || '';
}

function findShapeEl(container, shapeId) {
  if (!container || !shapeId) return null;
  return container.querySelector(
    `.tl-shape[data-shape-id="${CSS.escape(shapeId)}"]:not(.tl-shape-background)`
  );
}

function syncBoundHighlightDom(container, boundIds) {
  if (!container) return;
  const wanted = new Set(boundIds);

  container.querySelectorAll(`.tl-shape.${BOUND_HIGHLIGHT_CLASS}`).forEach(el => {
    if (!wanted.has(shapeDomId(el))) {
      el.classList.remove(BOUND_HIGHLIGHT_CLASS);
    }
  });

  for (const id of wanted) {
    findShapeEl(container, id)?.classList.add(BOUND_HIGHLIGHT_CLASS);
  }
}

/**
 * Collect endpoint shape ids for every selected connecting line.
 */
export function getSelectedArrowBoundShapeIds(editor) {
  if (!editor) return [];
  const selected = editor.getSelectedShapeIds?.() || [];
  const ids = [];
  const seen = new Set();
  for (const id of selected) {
    if (editor.getShape(id)?.type !== 'arrow') continue;
    for (const boundId of getArrowBoundShapeIds(editor, id)) {
      if (seen.has(boundId)) continue;
      seen.add(boundId);
      ids.push(boundId);
    }
  }
  return ids;
}

/**
 * When the user selects a connecting line, outline its bound endpoints with a
 * CSS border — do not add those shapes to the tldraw selection.
 */
export function registerArrowSelectConnected(editor) {
  if (!editor?.store?.listen) return () => {};

  const container = editor.getContainer?.() || null;
  let rafId = null;
  let highlightedIds = [];

  const scheduleSync = () => {
    if (rafId != null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      syncBoundHighlightDom(container, highlightedIds);
    });
  };

  const updateFromSelection = () => {
    highlightedIds = getSelectedArrowBoundShapeIds(editor);
    scheduleSync();
  };

  const unlistenSession = editor.store.listen(updateFromSelection, { scope: 'session' });
  const unlistenDocument = editor.store.listen(
    () => {
      if (highlightedIds.length) scheduleSync();
    },
    { scope: 'document' }
  );

  updateFromSelection();

  return () => {
    if (rafId != null) cancelAnimationFrame(rafId);
    unlistenSession();
    unlistenDocument();
    syncBoundHighlightDom(container, []);
  };
}
